import * as Sentry from '@sentry/nextjs'
import axios, { type AxiosResponse, isAxiosError } from 'axios'
import type { User } from 'firebase/auth'

import { baseAxiosConfig } from '@/lib/api/rest/config'
import { copyRequestCorrelation, enrichBackendRequestEvent } from '@/lib/api/rest/requestCorrelation'
import { getUserAuthHeaders } from '@/lib/getUserAuthHeaders'

import { CheckoutSessionCreationErrorResponseType } from '@/clients/payment-service'

import {
  type ConfirmIntentCheckoutRequest,
  type ConfirmIntentCheckoutResponse,
  type CreateIntentCheckoutRequest,
  INTENT_CONFLICT_CODES,
  type IntentCheckoutError,
  type IntentCheckoutResponse,
  type IntentConflictCode,
  type UpdateIntentCheckoutRequest,
} from './types'

const client = axios.create({ ...baseAxiosConfig, baseURL: process.env.NEXT_PUBLIC_PAYMENT_SERVICE_BASE })

const INTENT_CHECKOUT_PATH = '/public/checkout/intent'

const isConflictCode = (code: unknown): code is IntentConflictCode =>
  INTENT_CONFLICT_CODES.includes(code as IntentConflictCode)

type ProblemBody = {
  detail?: string | null
  error_code?: string | null
  additional_details?: string | null
}

const intentPath = (id: string, suffix = '') => `${INTENT_CHECKOUT_PATH}/${encodeURIComponent(id)}${suffix}`

export class IntentCheckoutRequestError extends Error {
  readonly error: IntentCheckoutError

  constructor(error: IntentCheckoutError) {
    super(`Intent checkout request rejected with ${error.status}`)
    this.name = 'IntentCheckoutRequestError'
    this.error = error
  }
}

export const toIntentCheckoutError = (caught: unknown): IntentCheckoutError | null => {
  if (!isAxiosError(caught)) return null

  const body = (caught.response?.data ?? {}) as ProblemBody
  const message = body.detail ?? null

  switch (caught.response?.status) {
    case 402:
      return { status: 402, code: 'CARD_DECLINED', message, declineCode: body.additional_details ?? null }
    case 409:
      // A 409 without a known code is not one the form can act on.
      return isConflictCode(body.error_code) ? { status: 409, code: body.error_code, message } : null
    case 410:
      return { status: 410, code: body.error_code ?? null, message }
    case 422:
      // A US address Stripe cannot tax: the customer fixes the address, so it is not a rejection of the basket.
      if (body.error_code === 'TAX_LOCATION_INVALID') return { status: 422, code: body.error_code, message }
      // 422 codes share their names with `CheckoutSessionCreationErrorResponseType`, so the session copy applies.
      return {
        status: 422,
        code:
          (body.error_code as CheckoutSessionCreationErrorResponseType | null | undefined) ??
          CheckoutSessionCreationErrorResponseType.CHECKOUT_VALIDATION_ERROR,
        message,
      }
    case 503:
      // Stripe could not calculate tax; payment-service released the checkout, so the outcome is known.
      return body.error_code === 'TAX_CALCULATION_FAILED' ? { status: 503, code: body.error_code, message } : null
    default:
      return null
  }
}

type IntentEndpoint = 'create' | 'update' | 'get' | 'confirm' | 'success'

/**
 * Every 4xx and 5xx payment-service answers, expected ones included, as one Sentry event grouped by endpoint, status
 * and code, so their volumes can be counted. 5xx at error level, 4xx at warning.
 */
const reportErrorResponse = (endpoint: IntentEndpoint, caught: unknown) => {
  if (!isAxiosError(caught) || !caught.response || caught.response.status < 400) return
  const { status } = caught.response
  const code = (caught.response.data as ProblemBody | undefined)?.error_code ?? 'none'
  Sentry.withScope((scope) => {
    scope.setTags({
      checkout_path: 'intent',
      'intent.endpoint': endpoint,
      'intent.status': status,
      'intent.error_code': code,
    })
    scope.setFingerprint(['intent-checkout-response', endpoint, String(status), code])
    scope.addEventProcessor((event) => enrichBackendRequestEvent(event, caught))
    // A `logentry` rather than a message, so Sentry's Dedupe does not drop a repeat of the same answer.
    Sentry.captureEvent({
      level: status >= 500 ? 'error' : 'warning',
      logentry: { message: `Intent checkout ${endpoint} answered ${status} ${code}` },
    })
  })
}

const send = async <T>(
  user: User,
  endpoint: IntentEndpoint,
  call: (headers: Awaited<ReturnType<typeof getUserAuthHeaders>>) => Promise<AxiosResponse<T>>,
): Promise<T> => {
  const headers = await getUserAuthHeaders(user)
  try {
    return (await call(headers)).data
  } catch (caught) {
    reportErrorResponse(endpoint, caught)
    const error = toIntentCheckoutError(caught)
    throw error ? copyRequestCorrelation(new IntentCheckoutRequestError(error), caught) : caught
  }
}

export const createIntentCheckout = (user: User, request: CreateIntentCheckoutRequest) =>
  send(user, 'create', (headers) => client.post<IntentCheckoutResponse>(INTENT_CHECKOUT_PATH, request, { headers }))

export const updateIntentCheckout = (user: User, id: string, request: UpdateIntentCheckoutRequest) =>
  send(user, 'update', (headers) => client.put<IntentCheckoutResponse>(intentPath(id), request, { headers }))

export const getIntentCheckout = (user: User, id: string) =>
  send(user, 'get', (headers) => client.get<IntentCheckoutResponse>(intentPath(id), { headers }))

export const confirmIntentCheckout = (user: User, id: string, request: ConfirmIntentCheckoutRequest) =>
  send(user, 'confirm', (headers) =>
    client.post<ConfirmIntentCheckoutResponse>(intentPath(id, '/confirm'), request, { headers }),
  )

/** A `409` means Stripe did not report success; the checkout status is unchanged. */
export const confirmIntentSuccess = async (user: User, id: string) => {
  try {
    return await send(user, 'success', (headers) =>
      client.post<IntentCheckoutResponse>(intentPath(id, '/success'), undefined, { headers }),
    )
  } catch (caught) {
    // This call's only conflict comes without a code.
    if (!isAxiosError(caught) || caught.response?.status !== 409) throw caught
    const message = (caught.response.data as ProblemBody | undefined)?.detail ?? null
    const error = new IntentCheckoutRequestError({ status: 409, code: 'PAYMENT_NOT_SUCCEEDED', message })
    throw copyRequestCorrelation(error, caught)
  }
}
